// Iter309 · C10 — Matriz de cumplimiento del pliego
// Relaciona requisito, cláusula, documento exigido, respuesta, evidencia y
// revisión por oferta. Criterio de aceptación del informe: "todo requisito
// obligatorio tiene responsable y estado" (se valida en el guardado) y
// "cambios de pliego muestran impacto en oferta y checklist" (al editar un
// requisito existente queda constancia de quién y cuándo lo revisó).
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ClipboardList, Link2, Sparkles, AlertTriangle } from "lucide-react";

const ESTADO_META = {
  cumple:     { label: "Cumple",     cls: "bg-green-100 text-green-700 border-green-300" },
  no_cumple:  { label: "No cumple",  cls: "bg-rose-100 text-rose-700 border-rose-300" },
  pendiente:  { label: "Pendiente",  cls: "bg-amber-100 text-amber-700 border-amber-300" },
  no_aplica:  { label: "No aplica",  cls: "bg-gray-100 text-gray-500 border-gray-300" },
};

const emptyItem = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  requisito: "", clausula: "", documento_exigido: "", obligatorio: true,
  estado: "pendiente", respuesta: "", evidencia_url: "", responsable: "",
  fecha_revision: "", observaciones: "",
});

export default function ComplianceMatrix({ oid }) {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({ total_obligatorios: 0, cumplidos_obligatorios: 0 });
  const [solvencias, setSolvencias] = useState([]);
  const [sugeridas, setSugeridas] = useState(null);

  const load = () => {
    axios.get(`${API}/ofertas-comercial/${oid}/cumplimiento`)
      .then(r => { setItems(r.data.items || []); setSummary(r.data); })
      .catch(() => toast.error("Error cargando matriz de cumplimiento"));
  };
  useEffect(() => {
    if (!oid) return;
    load();
    axios.get(`${API}/ofertas-comercial/solvencias/catalogo`).then(r => setSolvencias(r.data || [])).catch(() => {});
    axios.get(`${API}/ofertas-comercial/${oid}/solvencias-sugeridas`).then(r => setSugeridas(r.data?.sugeridas || [])).catch(() => setSugeridas([]));
  }, [oid]);

  // Trae una capacidad propia sugerida a la matriz: la vincula al primer
  // requisito obligatorio sin evidencia; si no hay, crea una fila nueva.
  const aplicarSugerencia = (s) => {
    if (!s.vigente) { toast.error("Esa solvencia está caducada: no se usa como prueba"); return; }
    setItems(prev => {
      const idx = prev.findIndex(it => it.obligatorio && !it.solvencia_catalogo_id);
      const target = idx >= 0 ? idx : prev.findIndex(it => !it.solvencia_catalogo_id);
      if (target >= 0) {
        return prev.map((it, i) => i === target
          ? { ...it, solvencia_catalogo_id: s.id, evidencia_url: s.evidencia_url || it.evidencia_url, estado: it.estado === "pendiente" ? "cumple" : it.estado }
          : it);
      }
      return [...prev, { ...emptyItem(), requisito: s.titulo, estado: "cumple", solvencia_catalogo_id: s.id, evidencia_url: s.evidencia_url || "", respuesta: `Acreditado mediante ${s.titulo}` }];
    });
    toast.success(`«${s.titulo}» vinculada a la matriz. Revisa alcance y guarda.`);
  };

  const update = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    const incomplete = items.find(it => it.obligatorio && !(it.responsable || "").trim());
    if (incomplete) {
      toast.error(`Falta responsable en requisito obligatorio: «${incomplete.requisito || "(sin título)"}»`);
      return;
    }
    const empty = items.find(it => !(it.requisito || "").trim());
    if (empty) { toast.error("Todo requisito necesita un título"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/ofertas-comercial/${oid}/cumplimiento`, { items });
      toast.success("Matriz de cumplimiento guardada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (!oid) return <p className="text-xs text-gray-400">Guarda la oferta primero para gestionar su matriz de cumplimiento.</p>;
  if (items === null) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  const pct = summary.total_obligatorios > 0 ? Math.round((summary.cumplidos_obligatorios / summary.total_obligatorios) * 100) : null;

  return (
    <div className="space-y-3" data-testid="compliance-matrix">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><ClipboardList size={15} className="text-[#007934]"/> Matriz de cumplimiento del pliego</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {pct == null ? "Sin requisitos obligatorios aún" : (
              <span data-testid="compliance-summary">
                Requisitos obligatorios cumplidos: <b className={pct === 100 ? "text-green-600" : "text-amber-600"}>{summary.cumplidos_obligatorios}/{summary.total_obligatorios}</b> ({pct}%)
              </span>
            )}
          </p>
        </div>
        <button onClick={addItem} data-testid="compliance-add-btn" className="text-xs font-semibold border border-[#007934] text-[#007934] rounded-lg px-3 py-1.5 hover:bg-[#007934]/5 flex items-center gap-1.5">
          <Plus size={13}/> Añadir requisito
        </button>
      </div>

      {sugeridas && sugeridas.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="solvencias-sugeridas">
          <p className="text-[11px] font-semibold text-emerald-800 flex items-center gap-1.5">
            <Sparkles size={13}/> Solvencias propias que podrían cubrir esta oferta
          </p>
          <div className="flex flex-wrap gap-2">
            {sugeridas.map(s => (
              <button key={s.id} onClick={() => aplicarSugerencia(s)} data-testid={`sugerida-${s.id}`}
                disabled={!s.vigente}
                title={`Coincide en: ${(s.match_terminos || []).join(", ")}`}
                className={`text-left text-[11px] rounded-lg border px-2.5 py-1.5 max-w-[260px] transition-colors ${s.vigente ? "bg-white border-emerald-300 hover:border-emerald-500" : "bg-rose-50 border-rose-200 opacity-70 cursor-not-allowed"}`}>
                <span className="font-semibold text-gray-800 line-clamp-1 flex items-center gap-1">
                  <Link2 size={10} className="text-emerald-600 flex-shrink-0"/> {s.titulo}
                </span>
                <span className="text-[10px] text-gray-500">
                  {!s.vigente ? <span className="text-rose-600 font-semibold inline-flex items-center gap-0.5"><AlertTriangle size={9}/> caducada — no usar</span>
                    : s.es_piloto ? <span className="text-amber-600 font-semibold">piloto — no es capacidad general</span>
                    : `coincide: ${(s.match_terminos || []).slice(0, 3).join(", ")}`}
                </span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-emerald-700/80">Sugerencia basada en coincidencia de términos. Verifica vigencia y alcance antes de usarla como evidencia.</p>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center text-xs text-gray-400 py-6 border border-dashed border-gray-200 rounded-lg">Sin requisitos cargados. Añade el primero del pliego.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it.id} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-white" data-testid={`compliance-item-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input value={it.requisito} onChange={e => update(idx, { requisito: e.target.value })} placeholder="Requisito (ej: solvencia técnica mínima)" data-testid={`compliance-requisito-${idx}`} className="md:col-span-2 border border-gray-200 rounded px-2 py-1.5 text-xs"/>
                <input value={it.clausula} onChange={e => update(idx, { clausula: e.target.value })} placeholder="Cláusula del pliego (ej: Anexo III.2)" className="border border-gray-200 rounded px-2 py-1.5 text-xs"/>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input value={it.documento_exigido} onChange={e => update(idx, { documento_exigido: e.target.value })} placeholder="Documento exigido" className="border border-gray-200 rounded px-2 py-1.5 text-xs"/>
                <input value={it.respuesta} onChange={e => update(idx, { respuesta: e.target.value })} placeholder="Respuesta / cómo se cumple" className="border border-gray-200 rounded px-2 py-1.5 text-xs"/>
                <input value={it.responsable} onChange={e => update(idx, { responsable: e.target.value })} placeholder={it.obligatorio ? "Responsable (obligatorio)" : "Responsable"} data-testid={`compliance-responsable-${idx}`} className={`border rounded px-2 py-1.5 text-xs ${it.obligatorio && !it.responsable ? "border-rose-300 bg-rose-50" : "border-gray-200"}`}/>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={it.estado} onChange={e => update(idx, { estado: e.target.value })} data-testid={`compliance-estado-${idx}`} className={`text-[11px] font-semibold px-2 py-1 rounded border ${ESTADO_META[it.estado]?.cls}`}>
                  {Object.entries(ESTADO_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <label className="flex items-center gap-1 text-[11px] text-gray-600">
                  <input type="checkbox" checked={it.obligatorio} onChange={e => update(idx, { obligatorio: e.target.checked })} data-testid={`compliance-obligatorio-${idx}`}/>
                  Obligatorio
                </label>
                <select
                  value={it.solvencia_catalogo_id || ""}
                  onChange={e => update(idx, { solvencia_catalogo_id: e.target.value || null, evidencia_url: solvencias.find(s => s.id === e.target.value)?.evidencia_url || it.evidencia_url })}
                  className="text-[11px] border border-gray-200 rounded px-2 py-1 bg-white flex-1 min-w-[160px]"
                  data-testid={`compliance-solvencia-link-${idx}`}
                >
                  <option value="">— Vincular solvencia del catálogo —</option>
                  {solvencias.filter(s => (s.clase || "propio") !== "requisito").map(s => (
                    <option key={s.id} value={s.id} disabled={!s.vigente}>
                      {s.titulo}{!s.vigente ? " (caducada)" : s.es_piloto ? " (piloto)" : ""}
                    </option>
                  ))}
                </select>
                <input type="date" value={it.fecha_revision || ""} onChange={e => update(idx, { fecha_revision: e.target.value })} className="text-[11px] border border-gray-200 rounded px-2 py-1"/>
                <button onClick={() => removeItem(idx)} data-testid={`compliance-remove-${idx}`} className="text-gray-400 hover:text-rose-600 ml-auto"><Trash2 size={14}/></button>
              </div>
              {it.solvencia_catalogo_id && (() => {
                const s = solvencias.find(x => x.id === it.solvencia_catalogo_id);
                if (!s) return null;
                return (
                  <p className="text-[10px] text-gray-500 flex items-center gap-1"><Link2 size={10}/> Evidencia reutilizada del catálogo: <b>{s.titulo}</b>{s.es_piloto && <span className="text-amber-600 font-semibold">(piloto — no es capacidad general en producción)</span>}</p>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="compliance-save-btn" className="text-xs bg-[#007934] text-white px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1.5">
          {saving && <Loader2 className="animate-spin" size={12}/>} Guardar matriz de cumplimiento
        </button>
      </div>
    </div>
  );
}
